import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useKeywords } from '@/lib/hooks/use-keywords';
import { Keyword } from '@/types/custom';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Loading from '@/components/loading';

interface NewsKeywordManagerProps {
  newsItemId: string;
}

export function NewsKeywordManager({ newsItemId }: NewsKeywordManagerProps) {
  const {
    keywords,
    getKeywordsForNewsItem,
    addKeywordToNewsItem,
    removeKeywordFromNewsItem,
  } = useKeywords();
  const [newsKeywords, setNewsKeywords] = useState<Keyword[]>([]);
  const [selectedKeywordId, setSelectedKeywordId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNewsKeywords();
  }, [newsItemId]);

  const loadNewsKeywords = async () => {
    setLoading(true);
    const data = await getKeywordsForNewsItem(newsItemId);
    setNewsKeywords(data);
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!selectedKeywordId) return;
    const success = await addKeywordToNewsItem(newsItemId, selectedKeywordId);
    if (success) {
      await loadNewsKeywords();
      setSelectedKeywordId('');
    }
  };

  const handleRemove = async (keywordId: string) => {
    const success = await removeKeywordFromNewsItem(newsItemId, keywordId);
    if (success) {
      await loadNewsKeywords();
    }
  };

  const availableKeywords = keywords.filter(
    (k) => !newsKeywords.some((nk) => nk.id === k.id),
  );

  return (
    <div className="space-y-1">
      <h3 className="text-sm font-medium">Keywords</h3>
      <div className="flex flex-wrap gap-1 min-h-[32px] p-1 border rounded-md bg-white">
        {loading ? (
          <Loading />
        ) : newsKeywords.length === 0 ? (
          <p className="text-xs text-muted-foreground">Geen keywords toegewezen</p>
        ) : (
          newsKeywords.map((keyword) => (
            <Badge key={keyword.id} variant="secondary" className="flex items-center gap-1 py-0.5 px-1.5 text-xs">
              {keyword.name}
              <button onClick={() => handleRemove(keyword.id)} className="ml-0.5 hover:bg-muted rounded-full">
                <X className="h-2 w-2" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <div className="flex gap-1 items-center mt-1">
        <Select value={selectedKeywordId} onValueChange={setSelectedKeywordId}>
          <SelectTrigger className="w-[180px] h-7 text-xs">
            <SelectValue placeholder="Selecteer keyword" />
          </SelectTrigger>
          <SelectContent>
            {availableKeywords.length === 0 ? (
              <SelectItem value="no-keywords" disabled className="text-xs">Geen beschikbare keywords</SelectItem>
            ) : (
              availableKeywords.map((k) => (
                <SelectItem key={k.id} value={k.id} className="text-xs">{k.name}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <Button onClick={handleAdd} disabled={!selectedKeywordId} size="sm" className="h-7 text-xs px-3">
          Toevoegen
        </Button>
      </div>
    </div>
  );
}
